import _88x31Image from "@/assets/88x31.png";
import { Boilerplate } from "@/components/Boilerplate";
import { Codeblock } from "@/components/Codeblock/Codeblock";
import { Box } from "@/components/layout/Box";
import { TabBar, TabBarPosition } from "@/components/layout/TabBar";
import { Link, TextLink } from "@/components/Links";
import { Text } from "@/components/Text";
import { loadGrammar, loadTheme } from "@/utils/shiki";
import { Language } from "@/utils/textmate";
import { TextmateTheme } from "@/utils/textmate/theme";
import { createFileRoute } from "@tanstack/react-router";

import htmlExampleContent from "./-sample.html?raw";
// oxlint-disable-next-line import/default -- oxlint bug
import _reactExampleContent from "./-sample.tsx?raw";

import { z } from "zod";

const reactExampleContent = _reactExampleContent.trimEnd();

function Sadan88x31Button() {
    return (
        <Link
            to="/{-$tab}"
            params={{
                tab: "about",
            }}
            rel="nofollow"
            className="size-88x31"
        >
            <img
                src={_88x31Image}
                className="size-88x31 pixelated"
            />
        </Link>
    );
}

const buttonPageParamsSchema = z.object({
    lang: z.enum(["html", "react"]).catch("html"),
});

type ButtonPageParams = z.infer<typeof buttonPageParamsSchema>;

export const Route = createFileRoute("/_/88x31/$lang")({
    component: ButtonPage,
    wrapInSuspense: true,
    params: {
        parse(rawParams): ButtonPageParams {
            return buttonPageParamsSchema.parse(rawParams);
        },
    },
    async loader({ params: { lang } }) {
        if (!import.meta.env.SSR) {
            switch (lang) {
                case "html": {
                    await Promise.all([loadGrammar(Language.HTML), loadTheme(TextmateTheme.TOKYO_NIGHT)]);
                    break;
                }
                case "react": {
                    await Promise.all([loadGrammar(Language.TYPESCRIPT_REACT), loadTheme(TextmateTheme.TOKYO_NIGHT)]);
                    break;
                }
            }
        }
    },
    staticData: {
        description: "My 88x31 button",
        imageUrl: "/assets/88x31.png",
        pageTitle: "88x31",
    },
});

// 88x31 button page
function ButtonPage() {
    const { lang } = Route.useParams();

    return (
        <>
            <Boilerplate />
            <div className="mt-4 flex w-full justify-center">
                <Box className="mr-2 w-full md:w-2/3">
                    <Text
                        size="3xl"
                        color="primary"
                        center
                    >
                        Add My Button
                    </Text>
                    <Sadan88x31Button />
                    <TabBar
                        selectedTab={lang}
                        tabsPosition={TabBarPosition.LEFT}
                        tabs={[
                            {
                                id: "html",
                                renderTab() {
                                    return (
                                        <TextLink
                                            to="/88x31/$lang"
                                            params={{ lang: "html" }}
                                        >
                                            HTML
                                        </TextLink>
                                    );
                                },
                                render() {
                                    return (
                                        <Codeblock lang={Language.HTML}>
                                            {htmlExampleContent}
                                        </Codeblock>
                                    );
                                },
                            },
                            {
                                id: "react",
                                renderTab() {
                                    return (
                                        <TextLink
                                            to="/88x31/$lang"
                                            params={{ lang: "react" }}
                                        >
                                            React
                                        </TextLink>
                                    );
                                },
                                render() {
                                    return (
                                        <Codeblock lang={Language.TYPESCRIPT_REACT}>
                                            {reactExampleContent}
                                        </Codeblock>
                                    );
                                },
                            },
                        ]}
                    />
                </Box>
            </div>
        </>
    );
}
